/** Session flag so one deploy only reloads the tab once. */
export const DEPLOY_RELOAD_STORAGE_KEY = 'eaze_deploy_reload_for';

/**
 * Deployed builds outside the local dev server should pick up a new release.
 * Local `ng serve` rebuilds constantly and must not reload the tab on every save.
 * @param envName - `environment.env`
 * @returns Whether this environment should reload after a deploy
 */
export function deployRefreshEnabled(envName: string): boolean {
  return envName !== 'local';
}

/**
 * Whether a failed lazy-chunk load is the stale-build failure.
 * Browsers phrase the missing hashed file differently.
 * @param error - Caught navigation or unhandled error
 * @returns True when the running app asked for a build file that is no longer deployed
 */
export function isStaleBuildError(error: unknown): boolean {
  const text = errorText(error);
  return (
    text.includes('Failed to fetch dynamically imported module') ||
    text.includes('error loading dynamically imported module') ||
    text.includes('Importing a module script failed') ||
    text.includes('ChunkLoadError') ||
    /Loading chunk [\w.-]+ failed/.test(text)
  );
}

/**
 * Entry script file name from a deployed `index.html`.
 * @param html - Document HTML
 * @returns `main` bundle file name, or null when the document has none
 */
export function entryScriptNameFromHtml(html: string): string | null {
  const match = html.match(
    /(?:src|href)=["'](?:[^"']*\/)?(main[^/"']*\.js)["']/
  );
  return match?.[1] ?? null;
}

/**
 * Entry script file name from a script URL already on the page.
 * @param src - Script `src`
 * @returns `main` bundle file name, or null when this script is not the entry
 */
export function entryScriptNameFromSrc(src: string): string | null {
  const path = src.split('?')[0]?.split('#')[0] ?? '';
  const name = path.split('/').pop() ?? '';
  return /^main[^/]*\.js$/.test(name) ? name : null;
}

/**
 * Same address with a cache-busting query so the browser requests the new document.
 * @param href - Current page address
 * @param now - Milliseconds used as the cache buster
 * @returns Address to load
 */
export function cacheBustedHref(href: string, now: number): string {
  const url = new URL(href);
  url.searchParams.set('_', String(now));
  return url.toString();
}

/**
 * Decides whether this tab should load the deployed build.
 * A target already stored means that reload was already attempted.
 * @param storedTarget - Previous reload target from session storage
 * @param target - Deployed entry file, or `chunk` after a failed lazy load
 * @returns Whether to navigate
 */
export function shouldReloadForDeploy(
  storedTarget: string | null,
  target: string
): boolean {
  return storedTarget !== target;
}

/**
 * Flattens an error and its cause into one searchable string.
 * @param error - Caught value
 * @returns Combined messages
 */
function errorText(error: unknown): string {
  if (error == null) {
    return '';
  }
  if (typeof error === 'string') {
    return error;
  }
  if (error instanceof Error) {
    return `${error.message}\n${errorText(error.cause)}`;
  }
  if (typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    const cause =
      'cause' in error ? (error as { cause?: unknown }).cause : undefined;
    const messageText = typeof message === 'string' ? message : '';
    return `${messageText}\n${errorText(cause)}`;
  }
  return '';
}
